import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import {ThemePalette} from '@angular/material/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {MobicredService} from 'src/app/services/mobicred/mobicred.service';
import {InitializePaymentResponse} from 'src/app/interfaces/mobicred/InitializePaymentResponse';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Session} from 'src/app/interfaces/session-data';
import {HttpErrorResponse} from '@angular/common/http';
import { DirtyErrorStateMatcherDirective } from 'src/app/directives/dirty-error-state-matcher.directive';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { SessionService } from 'src/app/services/session/session.service';

@Component({
  selector: 'app-mobicred-login-version-two',
  templateUrl: './mobicred-login.component.html',
  styleUrls: ['./mobicred-login.component.scss']
})
export class MobicredLoginComponent implements OnInit {
  private baseUrl: string;
  private merchantID: string = "";
  private redirectUrl = encodeURIComponent(window.location.href + '?sessionToken=' + sessionStorage.getItem('sessionToken'));
  public mobicredUrl = "";


  public paymentOptionsLoadedJustify = 'space-between !important';
  public color: ThemePalette = 'primary';
  public diameter = 50;

  public headerColor = '';

  matcher = new DirtyErrorStateMatcherDirective();

  // form
  loginForm: FormGroup;
  // static
  public username: string;
  public password: string;

  mobile;
  showErrors = true;
  unauthedUsername = false;
  session: Session;

  formErrors = {
    username: '',
    password: '',
  };

  validationMessages = {
    username: {
      required: 'Username is required',
      email: 'Please enter a valid email',
      username: 'Please enter a valid username'
    },
    password: {
      required: 'Password is required',
      minLength: 'Password must be at least 3 characters long'
    }
  };

  public hide = true;

  // loading vars
  public loading = false;
  public login = false;
  @Output()
  notify: EventEmitter<{}> = new EventEmitter<{ undefined }>();
  startTimer;
  timer = 300;

  constructor(
    private router: Router,
    private mobicredService: MobicredService,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private _snackBar: MatSnackBar,
    private breakpointObserver: BreakpointObserver,
    private sessionService:SessionService
  ) { 
    this.merchantID = "";
    this.mobicredUrl = "";
    this.mobicredService.retrieveMerchantConfig(sessionStorage.getItem('sessionToken')).subscribe((val:{merchantId:string,registerUrl:string})=>{
      this.merchantID = val.merchantId;
      this.mobicredUrl = val.registerUrl + '&returnUrl=' + this.redirectUrl;
    });
    this.baseUrl = this.sessionService.getVirtualTwoBaseurl();
    this.createForm();
    breakpointObserver.observe([
      Breakpoints.HandsetLandscape,
      Breakpoints.HandsetPortrait
    ]).subscribe(result => {
      this.mobile = result.matches;
    });
  }

  ngOnInit(): void {
    this.session = JSON.parse(sessionStorage.getItem('sessionData'));
  }

  getColorScheme(): string {
    if (this.session && this.session.sessionDisplayInformation && this.session.sessionDisplayInformation.colorScheme) {
      return this.session.sessionDisplayInformation.colorScheme.replace(';', '');
    }
    return 'inherit';
  }

  getTextColor(): string {
    if (this.session && this.session.sessionDisplayInformation && this.session.sessionDisplayInformation.colorScheme) {
      let bgColor:string = this.session.sessionDisplayInformation.colorScheme.replace(';', '');
      let color = bgColor.charAt(0) === '#' ? bgColor.substring(1, 7) : bgColor;
      let r = parseInt(color.substring(0, 2), 16); // Red
      let g = parseInt(color.substring(2, 4), 16); // Green
      let b = parseInt(color.substring(4, 6), 16); // Blue

      // Calculate the perceptive luminance
      let luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

      // Return black for bright colors, white for dark colors
      return luminance > 0.5 ? '#000000' : '#FFFFFF';
    }
    return 'inherit';
  }

  get usernameControl() {
    return this.loginForm.get('username');
  }

  get passwordControl() {
    return this.loginForm.get('password');
  }

  openSnackBar(message: string, action: string) {
    this._snackBar.open(message, action, {
      duration: 5000,
    });
  }

  resetValidation() {
    sessionStorage.setItem('formSubmitted', 'false');
  }

  formValidation() {
    if (this.usernameControl && this.usernameControl.value){
      this.unauthedUsername = false;
      this.showErrors = false;
      this.makeFieldsDirty();
      if (this.loginForm.valid) {
        this.mobicredLogin();
      }
    } else {
      this.usernameControl.setErrors(Validators.required);
      this.showErrors = true;
      return;
    }
  }

  createForm() {
    this.loginForm = this.fb.group({
      username: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(3)]],
    });
  }

  makeFieldsDirty() {
    Object.keys(this.loginForm.controls)
      .forEach(field => {
        const control = this.loginForm.get(field);
        control.markAsDirty({onlySelf: true});
        control.markAsTouched({onlySelf: true});
      });
  }

  mobicredLogin(){
    if (this.loginForm.valid) {
      sessionStorage.setItem('formSubmitted', 'true');
      this.loading = true;
      this.makeFieldsDirty();
      this.mobicredService.initializeTransaction(sessionStorage.getItem('sessionToken'), this.usernameControl.value, this.passwordControl.value).subscribe((data: InitializePaymentResponse) => {
          sessionStorage.setItem('mobicred-data', JSON.stringify(data));
          this.loading = false;
          this.router.navigate(['otp'], { relativeTo: this.route.parent });
      }, (err: HttpErrorResponse) => {
        if (err.status === 401) {
          this.openSnackBar('Invalid Password/Username combination', 'Try Again');
          this.showErrors = true;
          this.loading = false;
        } else {
          this.router.navigate([`${this.baseUrl}/transaction-status`]);
        }
      });
    } else {
      if (this.loginForm.controls.username.valid === false || this.loginForm.controls.username.touched === false) {
        this.loginForm.controls.username.setValue('');
      }
      if (this.loginForm.controls.password.valid === false || this.loginForm.controls.password.touched === false) {
        this.loginForm.controls.password.setValue('');
      }

    }

  }

  cancelTransaction() {
    this.router.navigate([`${this.baseUrl}/transaction-status/userCancelled`]);
  }

}
